import { useCallback, useEffect, useState } from "react";

type FavType = "role" | "news";

const STORAGE_KEY = "loli-favorites";

type FavMap = Record<FavType, string[]>;

const emptyFav: FavMap = { role: [], news: [] };

function readFavorites(): FavMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyFav;
    const parsed = JSON.parse(raw) as Partial<FavMap>;
    return {
      role: Array.isArray(parsed.role) ? parsed.role : [],
      news: Array.isArray(parsed.news) ? parsed.news : []
    };
  } catch {
    return emptyFav;
  }
}

function writeFavorites(map: FavMap) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore write errors */
  }
}

export function useFavorites() {
  const [favorites, setFavorites] = useState<FavMap>(emptyFav);

  useEffect(() => {
    setFavorites(readFavorites());
  }, []);

  const isFavorite = useCallback(
    (type: FavType, id: string) => favorites[type].includes(id),
    [favorites]
  );

  const toggleFavorite = useCallback((type: FavType, id: string) => {
    setFavorites((prev) => {
      const list = prev[type];
      const next = list.includes(id)
        ? list.filter((item) => item !== id)
        : [id, ...list];
      const map: FavMap = { ...prev, [type]: next };
      writeFavorites(map);
      return map;
    });
  }, []);

  const countOf = useCallback((type: FavType) => favorites[type].length, [favorites]);

  return { favorites, isFavorite, toggleFavorite, countOf };
}